import { differenceInSeconds } from 'date-fns';
import { useEffect, useEffectEvent, useMemo, useRef, useState } from 'react';
import { ExchangeRate, ExchangeRateCountdown } from '../../types';
import { isDefined, Loadable, LoadableState } from '../../utils';

type UseOrderExchangeCountdownCommonParams = {
  exchangeRate?: LoadableState<ExchangeRate>;
  onExchangeRateExpired: (exchangeRate: ExchangeRate, time: Date) => void;
  /** When set we pin the last (non-locked) exchangerate. Used when order submitting is in progress. */
  isExchangeRateLocked?: boolean;
};

type UseOrderExchangeCountdownOutput = {
  countdown?: ExchangeRateCountdown;
};

const ONE_SECOND = 1000;

export const useOrderExchangeCountdownCommon = ({
  exchangeRate: exchangeRateProp,
  isExchangeRateLocked = false,
  onExchangeRateExpired,
}: UseOrderExchangeCountdownCommonParams): UseOrderExchangeCountdownOutput => {
  const [secondsRemaining, setSecondsRemaining] = useState<number | undefined>(undefined);
  const [durationSeconds, setDurationSeconds] = useState<number | undefined>(undefined);
  const exchangeRateSnapshotRef = useRef<LoadableState<ExchangeRate> | undefined>(exchangeRateProp);

  if (!isExchangeRateLocked) {
    exchangeRateSnapshotRef.current = exchangeRateProp;
  }

  const exchangeRate = isExchangeRateLocked ? exchangeRateSnapshotRef.current : exchangeRateProp;

  const validUntil = useMemo<number | 'indefinite' | undefined>(() => {
    if (!isDefined(exchangeRate) || !Loadable.hasValue(exchangeRate)) {
      return undefined;
    }
    if (exchangeRate.validUntil === 'indefinite') {
      return 'indefinite';
    }
    return exchangeRate.validUntil.getTime();
  }, [exchangeRate]);

  const validFrom =
    isDefined(exchangeRate) && Loadable.hasValue(exchangeRate) ? exchangeRate.validFrom.getTime() : undefined;

  const handleExpired = useEffectEvent((): void => {
    if (isDefined(exchangeRate) && Loadable.hasValue(exchangeRate)) {
      onExchangeRateExpired(exchangeRate, new Date());
    }
  });

  useEffect(() => {
    if (!isDefined(validUntil) || validUntil === 'indefinite' || !isDefined(validFrom)) {
      setSecondsRemaining(undefined);
      setDurationSeconds(undefined);
      return;
    }

    const duration = differenceInSeconds(validUntil, validFrom);
    const remaining = differenceInSeconds(validUntil, new Date());
    setDurationSeconds(duration);
    setSecondsRemaining(remaining);

    if (remaining <= 0) {
      handleExpired();
      return;
    }

    // Align ticks to validUntil's ms offset so all instances decrement at the exact same moment
    const msOffset = validUntil % ONE_SECOND;
    const nowMs = Date.now() % ONE_SECOND;
    const untilNextTick = (msOffset - nowMs + ONE_SECOND) % ONE_SECOND || ONE_SECOND;
    let intervalId: ReturnType<typeof setInterval>;

    const timeoutId = setTimeout(() => {
      const remaining = differenceInSeconds(validUntil, new Date());
      setSecondsRemaining(remaining);

      if (remaining <= 0) {
        handleExpired();
        return;
      }

      intervalId = setInterval(() => {
        const remaining = differenceInSeconds(validUntil, new Date());
        setSecondsRemaining(remaining);

        if (remaining <= 0) {
          clearInterval(intervalId);
          handleExpired();
          return;
        }
      }, ONE_SECOND);
    }, untilNextTick);

    return () => {
      clearTimeout(timeoutId);
      clearInterval(intervalId);
    };
  }, [validUntil, validFrom]);

  const countdown = useMemo<ExchangeRateCountdown | undefined>(() => {
    if (validUntil === 'indefinite') {
      return 'indefinite';
    }
    const isTicking = isDefined(secondsRemaining) && isDefined(durationSeconds) && secondsRemaining > 0;
    return isTicking ? { secondsRemaining, durationSeconds } : undefined;
  }, [durationSeconds, secondsRemaining, validUntil]);

  return { countdown };
};
